<template>
  <!-- One dialog, five modes, for the trip card actions that used window.prompt:
       a typo there (ปิดรอบ's date) could free a truck early. It only collects and
       checks input; the page does the write and the SAP date, as before. -->
  <v-dialog :value="value" max-width="560" persistent @input="$emit('input', $event)">
    <v-card v-if="trip">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-subtitle class="pt-1">
        รอบ {{ trip.startDate }} → {{ trip.returnDate }} · รถ {{ trip.regNum }}
        <span v-if="order"> · {{ U.orderRef(order) }} {{ order.name }}</span>
      </v-card-subtitle>

      <v-card-text>
        <!-- + เพิ่มออเดอร์: tick any of the group's orders ready by the trip's week. -->
        <template v-if="mode === 'add'">
          <!-- A sent trip takes orders only until its loading day is over, and the
               send onto the sheet is not extended past it. -->
          <div v-if="trip.status === 'sent'" class="caption mb-2">ต้องส่งเพิ่มเข้าใบจัดรถให้เสร็จภายในวันโหลดของ ({{ U.loadingDay(trip.startDate) }})</div>
          <div v-if="!addable.length" class="body-2">
            ไม่มีออเดอร์ที่เพิ่มได้ — ต้องอยู่ในกลุ่มของรอบนี้ และลูกค้าพร้อมรับภายในสัปดาห์ที่ออกรถ
          </div>
          <div v-for="o in addable" :key="o.id" class="d-flex align-center add-row">
            <v-checkbox v-model="picked" :value="o.id" hide-details dense class="mt-0 pt-0" />
            <div class="flex-grow-1">
              <!-- The SO first: one customer can have several orders waiting. -->
              <div class="font-weight-medium">{{ U.orderRef(o) }} · {{ o.name }}</div>
              <div class="caption grey--text text--darken-1">
                {{ o.upProvince }} · {{ U.orderFigures(o, cfg).count }} ชิ้น · {{ cbm(U.orderFigures(o, cfg).volume) }} CBM
                · ตั้งแต่ {{ U.weekLabel(o.earliestWeek) }}
              </div>
            </div>
            <span class="text-no-wrap">฿{{ money(o.orderValue) }}</span>
          </div>
          <div v-if="picked.length" class="mt-3">
            รอบนี้จะเป็น ฿{{ money(addedValue) }} / ฿{{ money(cfg.minValue) }}
            <div>ปริมาณ {{ cbm(addedVolume) }} / {{ cbm(cfg.capacityCbm) }} CBM</div>
            <!-- A warning, never a refusal: staff may load past the truck's figure. -->
            <v-alert v-if="addedVolume > cfg.capacityCbm" type="warning" dense class="mt-2 mb-0">เกินความจุรถ — ยังเพิ่มได้</v-alert>
          </div>
        </template>

        <!-- เลื่อนไปรอบถัดไป: the customer's new week, from the weeks a new order could pick. -->
        <template v-else-if="mode === 'postpone'">
          <v-select v-model="week" :items="weeks" item-text="label" item-value="week"
            label="ลูกค้าพร้อมรับตั้งแต่สัปดาห์" outlined dense />
          <div class="caption">ออเดอร์จะออกจากรอบนี้ และกลับไปรอจัดรอบในสัปดาห์ที่เลือก</div>
        </template>

        <!-- แก้วันกลับ -->
        <template v-else-if="mode === 'return'">
          <v-text-field v-model="date" type="date" label="วันกลับ" outlined dense
            :min="trip.startDate" :hint="'ตามตาราง ' + U.daysOf(trip.groups) + ' วัน'" persistent-hint />
        </template>

        <!-- ปิดรอบ: the real return day frees the truck from then on. -->
        <template v-else-if="mode === 'close'">
          <v-text-field v-model="date" type="date" label="วันที่รถกลับจริง" outlined dense
            :min="trip.startDate" :max="today" />
          <div class="caption">รถคันนี้จะว่างตั้งแต่วันถัดจากวันที่รถกลับจริง (วันกลับตามแผน {{ trip.returnDate }})</div>
        </template>

        <!-- The handover list, when the clipboard is refused (e.g. plain http). -->
        <template v-else-if="mode === 'copy'">
          <div class="caption mb-1">คัดลอกอัตโนมัติไม่ได้ — เลือกข้อความทั้งหมดแล้วกด Ctrl+C</div>
          <v-textarea :value="text" outlined dense rows="10" readonly @focus="$event.target.select()" />
        </template>

        <v-alert v-if="problem" dense type="error" class="mt-3 mb-0">{{ problem }}</v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">{{ mode === 'copy' ? 'ปิด' : 'ยกเลิก' }}</v-btn>
        <v-btn v-if="mode !== 'copy'" color="primary" :disabled="!canSave" :loading="saving" @click="submit">
          {{ saveLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import U from '@/components/upcountry'
import R from '@/components/bookingRules'

const TITLES = {
  add: '+ เพิ่มออเดอร์เข้ารอบ',
  postpone: 'เลื่อนไปรอบถัดไป',
  return: 'แก้วันกลับ',
  close: 'ปิดรอบ',
  copy: 'รายการให้จัดรถ'
}
const SAVE = { add: 'เพิ่มเข้ารอบ', postpone: 'เลื่อน', return: 'บันทึก', close: 'ปิดรอบ' }

export default {
  name: 'upcountryTripDialog',
  props: {
    value: Boolean,
    mode: { type: String, required: true },
    trip: { type: Object, default: null },
    order: { type: Object, default: null },
    orders: { type: Array, default: () => [] },
    holds: { type: Array, default: () => [] },
    cfg: { type: Object, required: true },
    text: { type: String, default: '' },
    saving: Boolean
  },
  data () {
    return { U, picked: [], week: null, date: '', today: R.todayStr() }
  },
  computed: {
    title () { return TITLES[this.mode] || '' },
    saveLabel () { return SAVE[this.mode] || 'บันทึก' },
    addable () { return this.trip ? U.eligible(this.orders, this.trip.groups, this.trip.startDate) : [] },
    addedValue () {
      const picked = this.addable.filter(o => this.picked.indexOf(o.id) !== -1)
      return (Number(this.trip.value) || 0) + U.totals(picked, this.cfg).value
    },
    addedVolume () {
      const picked = this.addable.filter(o => this.picked.indexOf(o.id) !== -1)
      return (Number(this.trip.volume) || 0) + U.totals(picked, this.cfg).volume
    },
    weeks () { return U.weekOptions(this.today, R.BOOKING_HORIZON_DAYS) },
    // What is wrong with the input, in Thai, or '' when it can be saved.
    problem () {
      if (this.mode === 'return' && this.date) {
        const c = U.checkReturnDate(this.trip, this.date, this.holds)
        return c.ok ? '' : R.errorMessage(c.reason)
      }
      if (this.mode === 'close' && this.date) {
        const c = U.checkActualReturn(this.trip, this.date, this.today)
        return c.ok ? '' : R.errorMessage(c.reason)
      }
      return ''
    },
    canSave () {
      if (this.mode === 'add') return this.picked.length > 0
      if (this.mode === 'postpone') return !!this.week
      if (this.mode === 'return' || this.mode === 'close') return !!this.date && !this.problem
      return false
    }
  },
  watch: {
    // Fresh input each time the dialog opens.
    value: { immediate: true, handler (open) { if (open) this.reset() } }
  },
  methods: {
    reset () {
      this.today = R.todayStr()
      this.picked = []
      this.date = this.mode === 'return' ? (this.trip && this.trip.returnDate) || '' : this.mode === 'close' ? this.today : ''
      // A customer who moves usually moves by a week: the week after the trip's.
      const next = this.trip ? U.addDays(U.mondayOf(this.trip.startDate), 7) : null
      this.week = this.weeks.some(w => w.week === next) ? next : ((this.weeks[0] || {}).week || null)
    },
    submit () {
      if (!this.canSave) return
      if (this.mode === 'add') this.$emit('submit', { orderIds: this.picked.slice() })
      else if (this.mode === 'postpone') this.$emit('submit', { week: this.week })
      else this.$emit('submit', { date: this.date })
    },
    money (n) { return Number(n || 0).toLocaleString('th-TH') },
    cbm (n) { return (Number(n) || 0).toFixed(2) }
  }
}
</script>

<style scoped>
.add-row {
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
